import React, { useState, useEffect } from 'react';
import { 
  MessageSquare, Send, Check, X, ArrowRight, ArrowLeft,
  User, Star, Clock, RefreshCw, ShieldCheck, Euro
} from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";
import { Textarea } from './ui/textarea';
import { offersApi } from '../lib/api';
import { formatPrice, formatDateTime } from '../lib/utils';
import { toast } from 'sonner';
import { VerifiedBadge } from './VerifiedBadge';

const NegotiationPanel = ({ parcelId, senderBudget, userRole, onOfferAccepted }) => {
  const [offers, setOffers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCounterDialog, setShowCounterDialog] = useState(false);
  const [selectedOffer, setSelectedOffer] = useState(null);
  const [counterPrice, setCounterPrice] = useState('');
  const [counterMessage, setCounterMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchOffers();
  }, [parcelId]);

  const fetchOffers = async () => {
    try {
      const response = await offersApi.getOffers(parcelId);
      setOffers(response.data);
    } catch (error) {
      console.error('Error fetching offers:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAccept = async (offer) => {
    setSubmitting(true);
    try {
      await offersApi.acceptCounterOffer(parcelId, offer.id);
      toast.success('Offre acceptée! Mission confirmée.');
      fetchOffers();
      if (onOfferAccepted) onOfferAccepted();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
    } finally {
      setSubmitting(false);
    }
  };

  const handleReject = async (offer) => {
    setSubmitting(true);
    try {
      await offersApi.rejectOffer(parcelId, offer.id);
      toast.success('Offre refusée');
      fetchOffers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
    } finally {
      setSubmitting(false);
    }
  };

  const openCounterDialog = (offer) => {
    setSelectedOffer(offer);
    setCounterPrice(offer.current_price?.toString() || offer.carrier_price?.toString() || '');
    setCounterMessage('');
    setShowCounterDialog(true);
  };

  const handleCounterOffer = async () => {
    if (!counterPrice || parseFloat(counterPrice) <= 0) {
      toast.error('Veuillez entrer un prix valide');
      return;
    }
    
    setSubmitting(true);
    try {
      await offersApi.makeCounterOffer(
        parcelId, 
        selectedOffer.id, 
        parseFloat(counterPrice),
        counterMessage || undefined
      );
      toast.success('Contre-offre envoyée!');
      setShowCounterDialog(false);
      fetchOffers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
    } finally {
      setSubmitting(false);
    }
  };

  const getStatusBadge = (offer) => {
    if (offer.status === 'accepted') {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-1 bg-green-100 text-green-700 text-xs font-medium rounded-full">
          <Check className="w-3 h-3" /> Acceptée
        </span>
      );
    }
    if (offer.status === 'rejected') {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-1 bg-red-100 text-red-700 text-xs font-medium rounded-full">
          <X className="w-3 h-3" /> Refusée
        </span>
      );
    }
    
    const waitingFor = offer.waiting_for;
    if (waitingFor === 'sender') {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-1 bg-amber-100 text-amber-700 text-xs font-medium rounded-full">
          <Clock className="w-3 h-3" /> En attente de l&apos;expéditeur
        </span>
      );
    }
    return (
      <span className="inline-flex items-center gap-1 px-2 py-1 bg-blue-100 text-blue-700 text-xs font-medium rounded-full">
        <Clock className="w-3 h-3" /> En attente du transporteur
      </span>
    );
  };

  if (loading) {
    return (
      <div className="text-center py-8">
        <div className="spinner mx-auto"></div>
        <p className="text-slate-500 mt-2">Chargement des offres...</p>
      </div>
    );
  }

  const pendingOffers = offers.filter(o => o.status === 'pending');
  const otherOffers = offers.filter(o => o.status !== 'pending');

  return (
    <div className="space-y-4" data-testid="negotiation-panel">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="font-semibold text-slate-900">Négociation de prix</h3>
          <p className="text-sm text-slate-500">
            Budget expéditeur: <span className="font-medium text-orange-600">{formatPrice(senderBudget)}</span>
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={fetchOffers}>
          <RefreshCw className="w-4 h-4 mr-1" />
          Actualiser
        </Button>
      </div>

      {/* No offers */}
      {offers.length === 0 && (
        <div className="text-center py-8 bg-slate-50 rounded-lg">
          <MessageSquare className="w-12 h-12 text-slate-300 mx-auto mb-3" />
          <p className="text-slate-500">Aucune offre pour le moment</p>
          {userRole === 'carrier' && (
            <p className="text-sm text-slate-400 mt-1">
              Faites une offre pour ce colis!
            </p>
          )}
        </div>
      )}

      {/* Active negotiations */}
      {pendingOffers.length > 0 && (
        <div className="space-y-3">
          <h4 className="text-sm font-medium text-slate-700">Négociations en cours</h4>
          {pendingOffers.map((offer) => (
            <Card key={offer.id} className="border-orange-200 bg-orange-50/50" data-testid={`offer-${offer.id}`}>
              <CardContent className="p-4">
                {/* Carrier info */}
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 bg-slate-200 rounded-full flex items-center justify-center">
                    <User className="w-5 h-5 text-slate-600" />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-slate-900">{offer.carrier_name}</span>
                      <VerifiedBadge verified={offer.carrier_verified} size="sm" />
                    </div>
                    {offer.carrier_rating > 0 && (
                      <div className="flex items-center gap-1 text-sm text-slate-500">
                        <Star className="w-3 h-3 text-yellow-500 fill-yellow-500" />
                        {offer.carrier_rating?.toFixed(1)} ({offer.carrier_reviews} avis)
                      </div>
                    )}
                  </div>
                  {getStatusBadge(offer)}
                </div>

                {/* Price display */}
                <div className="flex items-center justify-between p-3 bg-white rounded-lg mb-3">
                  <div>
                    <p className="text-xs text-slate-500">Prix actuel</p>
                    <p className="text-2xl font-bold text-orange-600">
                      {formatPrice(offer.current_price || offer.carrier_price)}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-xs text-slate-500">vs Budget</p>
                    {(offer.current_price || offer.carrier_price) <= senderBudget ? (
                      <p className="text-green-600 font-medium">✓ Dans le budget</p>
                    ) : (
                      <p className="text-amber-600 font-medium">
                        +{formatPrice((offer.current_price || offer.carrier_price) - senderBudget)}
                      </p>
                    )}
                  </div>
                </div>

                {/* Negotiation history */}
                {offer.negotiation_history && offer.negotiation_history.length > 1 && (
                  <div className="mb-3">
                    <p className="text-xs text-slate-500 mb-2">Historique ({offer.negotiation_rounds || 0} tours)</p>
                    <div className="flex flex-wrap gap-2">
                      {offer.negotiation_history.map((entry, idx) => (
                        <div 
                          key={entry.id} 
                          className={`px-2 py-1 rounded text-xs ${
                            entry.by === 'carrier' 
                              ? 'bg-blue-100 text-blue-700' 
                              : 'bg-green-100 text-green-700'
                          }`}
                        >
                          {entry.by === 'carrier' ? '🚗' : '📦'} {formatPrice(entry.price)}
                          {idx < offer.negotiation_history.length - 1 && (
                            <ArrowRight className="w-3 h-3 inline ml-1" />
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Actions */}
                {offer.can_respond && offer.status === 'pending' && (
                  <div className="flex gap-2 pt-2 border-t border-orange-200">
                    <Button 
                      size="sm" 
                      className="flex-1 bg-green-500 hover:bg-green-600"
                      onClick={() => handleAccept(offer)}
                      disabled={submitting}
                      data-testid={`accept-offer-${offer.id}`}
                    >
                      <Check className="w-4 h-4 mr-1" />
                      Accepter
                    </Button>
                    <Button 
                      size="sm" 
                      variant="outline"
                      className="flex-1 border-orange-300 text-orange-600 hover:bg-orange-100"
                      onClick={() => openCounterDialog(offer)}
                      disabled={submitting}
                      data-testid={`counter-offer-${offer.id}`}
                    >
                      <Euro className="w-4 h-4 mr-1" />
                      Contre-offre
                    </Button>
                    <Button 
                      size="sm" 
                      variant="outline"
                      className="border-red-200 text-red-600 hover:bg-red-50"
                      onClick={() => handleReject(offer)}
                      disabled={submitting}
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                )}

                {!offer.can_respond && offer.status === 'pending' && (
                  <div className="text-center py-2 text-sm text-slate-500">
                    <Clock className="w-4 h-4 inline mr-1" />
                    En attente de réponse...
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Past offers */}
      {otherOffers.length > 0 && (
        <div className="space-y-2">
          <h4 className="text-sm font-medium text-slate-500">Offres passées</h4>
          {otherOffers.map((offer) => (
            <div 
              key={offer.id} 
              className={`p-3 rounded-lg border ${
                offer.status === 'accepted' 
                  ? 'bg-green-50 border-green-200' 
                  : 'bg-slate-50 border-slate-200'
              }`}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <span className="font-medium text-sm">{offer.carrier_name}</span>
                  <VerifiedBadge verified={offer.carrier_verified} size="sm" />
                </div>
                <div className="flex items-center gap-2">
                  <span className="font-bold">{formatPrice(offer.final_price || offer.current_price || offer.carrier_price)}</span>
                  {getStatusBadge(offer)}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Counter offer dialog */}
      <Dialog open={showCounterDialog} onOpenChange={setShowCounterDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Faire une contre-offre</DialogTitle>
            <DialogDescription>
              Proposez un nouveau prix à {selectedOffer?.carrier_name}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
              <span className="text-sm text-slate-600">Prix actuel:</span>
              <span className="font-bold text-lg">
                {formatPrice(selectedOffer?.current_price || selectedOffer?.carrier_price)}
              </span>
            </div>
            
            <div className="space-y-2">
              <label className="text-sm font-medium text-slate-700">Votre prix (€)</label>
              <Input
                type="number"
                step="0.50"
                min="1"
                value={counterPrice}
                onChange={(e) => setCounterPrice(e.target.value)}
                placeholder="Ex: 20.00"
                className="text-lg"
                data-testid="counter-price-input"
              />
            </div>
            
            <div className="space-y-2">
              <label className="text-sm font-medium text-slate-700">Message (optionnel)</label>
              <Textarea
                value={counterMessage}
                onChange={(e) => setCounterMessage(e.target.value)}
                placeholder="Ex: Je peux faire 18€ si enlèvement après 14h"
                rows={2}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCounterDialog(false)}>
              Annuler
            </Button>
            <Button 
              onClick={handleCounterOffer}
              disabled={submitting || !counterPrice}
              className="bg-orange-500 hover:bg-orange-600"
            >
              {submitting ? 'Envoi...' : 'Envoyer la contre-offre'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default NegotiationPanel;
